<template>
  <div>
    <div class="banners-wrapper">
      <SectionBanners />
    </div>
    <footer class="site-footer" id="footer" ref="footerEl" v-if="footer">
      <div class="footer-base">
        <img
          :src="footer.image"
          alt=""
        />
      </div>
      <div class="footer-heading" v-if="footer.companyName">
        {{ footer.companyName }}
      </div>
      <div class="footer-content">
        <div
          v-for="(col, idx) in orderedColumns"
          :key="col.key ?? `col-${idx}`"
          :class="getColumnClass(col)"
        >
          <h4 v-if="col.key !== 'menu'" class="footer-subtitle">
            {{ col.title }}
          </h4>
          <template v-if="col.type === 'menu'">
            <nav class="footer-menu">
              <ul>
                <li v-for="(item, i) in col.items" :key="i">
                  <a
                    :href="item.link"
                    :target="item.target || '_self'"
                    :rel="item.target === '_blank' ? 'noopener' : ''"
                  >
                    {{ item.label || item.text }}
                  </a>
                </li>
              </ul>
            </nav>
          </template>
          <template v-else>
            <div :class="col.key === 'contacts' ? 'contact-items' : 'society-items'">
              <div
                v-for="(item, i) in col.items"
                :key="i"
                :class="col.key === 'contacts' ? 'contact-item' : 'society-item'"
              >
                <template v-if="item.label && item.value">
                  <strong>{{ item.label }}</strong>
                  <template v-if="item.link">
                    <a
                      :href="item.link"
                      :target="item.target || (item.link?.startsWith('http') ? '_blank' : '_self')"
                      :rel="(item.target || '').includes('_blank') ? 'noopener' : ''"
                    >
                      {{ item.value }}
                    </a>
                  </template>
                  <template v-else>
                    {{ ' ' + item.value }}
                  </template>
                </template>
                <template v-else>
                  <template v-if="item.link">
                    <a
                      :href="item.link"
                      :target="item.target || (item.link?.startsWith('http') ? '_blank' : '_self')"
                      :rel="(item.target || '').includes('_blank') ? 'noopener' : ''"
                    >
                      {{ item.text }}
                    </a>
                  </template>
                  <template v-else>
                    {{ item.text }}
                  </template>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="footer-bottom-wrapper">
        <div class="footer-bottom">
          {{ footer.text }}
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { state } from '@/store/index.js'
import SectionBanners from '@/components/sections/shared/SectionBanners.vue'

// ───────────────────────────────────────────────────────────────────
// DATA & STORE
// ───────────────────────────────────────────────────────────────────

/** Footer content from global store */
const footer = computed(() => state.footer)

/**
 * Àncora footer: se si arriva con #footer nell'URL (es. link "Contatti" dalla
 * home esterna), scrolla al footer appena i dati async sono pronti.
 */
const footerEl = ref(null)

/**
 * Arrivo da link esterno con #footer (es. "Contatti" dalla landing):
 * porta in fondo alla pagina appena i dati REALI sono caricati, poi ripulisce
 * l'URL togliendo #footer. Lo scroll è ripetuto per assorbire il reflow dovuto
 * al caricamento asincrono di immagini/sezioni.
 */
let footerHandled = false
watch(footer, (val) => {
  if (footerHandled) return
  // ignora il placeholder iniziale { text: '' }: aspetta il footer reale
  if (!val || !(val.companyName || val.columns)) return
  if (window.location.hash !== '#footer') return
  footerHandled = true
  nextTick(() => {
    const toBottom = () => window.scrollTo({ top: document.documentElement.scrollHeight })
    toBottom()
    setTimeout(toBottom, 300)
    setTimeout(() => {
      toBottom()
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }, 800)
  })
}, { immediate: true })

// ───────────────────────────────────────────────────────────────────
// COLUMNS GENERATION
// ───────────────────────────────────────────────────────────────────

/**
 * Generates footer columns from store data
 * Returns predefined columns array or builds from footer.societyData and footer.contacts
 * 
 * Column types:
 * - society: Company information (Swift, Capital, etc.)
 * - contacts: Contact details (address, tel, email, PEC)
 * - menu: Center links menu
 * 
 * @returns {Array} Array of column objects with items and metadata
 */
const columns = computed(() => {
  const f = footer.value || {}
  
  // Use predefined columns if available
  if (Array.isArray(f.columns) && f.columns.length) return f.columns

  // Build society column items
  const societyItems = []
  if (f.societyData?.swift)    societyItems.push({ label: 'Swift/BIC:', value: f.societyData.swift })
  if (f.societyData?.capitale) societyItems.push({ text: f.societyData.capitale })
  if (f.societyData?.cf)       societyItems.push({ text: f.societyData.cf })
  if (f.societyData?.albo)     societyItems.push({ text: f.societyData.albo })
  if (f.societyData?.fondo)    societyItems.push({ text: f.societyData.fondo })

  // Build contact column items
  const contactItems = []
  if (f.contacts?.sede)  contactItems.push({ label: 'Sede Legale', value: f.contacts.sede })
  if (f.contacts?.tel)   contactItems.push({ text: `Tel. ${f.contacts.tel}${f.contacts?.fax ? ' - Fax ' + f.contacts.fax : ''}` })
  if (f.contacts?.email) contactItems.push({ label: 'Indirizzo e-mail:', value: f.contacts.email, link: `mailto:${f.contacts.email}` })
  if (f.contacts?.pec)   contactItems.push({ label: 'Indirizzo PEC:', value: f.contacts.pec, link: `mailto:${f.contacts.pec}` })
  if (f.contacts?.codice || f.contacts?.codiceDestinatario) {
    contactItems.push({ text: f.contacts.codice || f.contacts.codiceDestinatario })
  }

  // Build menu column items
  const menuItems = (f.centerLinks || []).map(l => ({
    label: l.label, link: l.link, target: l.target
  }))

  return [
    { key: 'society',  title: f.societyData?.title, type: 'list', order: 1, items: societyItems },
    { key: 'contacts', title: f.contacts?.title,     type: 'list', order: 2, items: contactItems },
    { key: 'menu',     title: undefined,             type: 'menu', order: 3, items: menuItems }
  ]
})

/**
 * Orders columns by priority
 * Maintains: Society (1) → Contacts (2) → Menu (3)
 * 
 * @returns {Array} Sorted column array
 */
const orderedColumns = computed(() =>
  [...columns.value].sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
)

// ───────────────────────────────────────────────────────────────────
// UTILITIES
// ───────────────────────────────────────────────────────────────────

/**
 * Maps column key to CSS grid area class
 * 
 * @param {Object} col - Column object with key property
 * @returns {String} CSS class name (footer-left, footer-center, footer-right)
 */
function getColumnClass(col) {
  const key = col.key || ''
  if (key === 'menu') return 'footer-left'
  if (key === 'contacts') return 'footer-right'
  return 'footer-center'
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";

/* ...STILI IDENTICI A QUELLI CHE MI HAI PASSATO... */
.banners-wrapper {
  margin-bottom: clamp(20px, 3vw, 60px);
}
@media (max-width: 1000px) {
  .banners-wrapper {
    margin-bottom: clamp(40px, 7vw, 90px);
  }
}
@media (max-width: 768px) {
  .banners-wrapper {
    margin-bottom: $space-5;
  }
}
@media (max-width: 480px) {
  .banners-wrapper {
    margin-bottom: $space-4;
  }
}
@media (max-width: 360px) {
  .banners-wrapper {
    margin-bottom: $space-3;
  }
}

/* Rimuove padding-top di section-banners su mobile e tablet */
.banners-wrapper ::v-deep(.section-banners) {
  @media (max-width: 1024px) {
    padding-top: 0 !important;
  }
}

.site-footer {
  position: relative;
  background: $footer-bg;
  color: $footer-text;
  font-size: 1rem;
  padding: $space-8 $space-1 $space-3 $space-1;
  text-align: center;

  .footer-base {
    position: absolute;
    left: 0;
    top: -1px;
    transform: translateY(calc(-100% + 2px));
    width: 100%;
    pointer-events: none;
    z-index: 3;

    img { 
      width: 100%;
      height: auto;
      display: block;
      margin-bottom: -2px;
    }
  }

  .footer-heading {
    max-width: 1800px;
    width: 98%;
    margin: 0 auto $space-2 auto;
    text-align: left;
    font-weight: 600;
    font-size: clamp(1.6rem, 3.5vw, 2rem);
    line-height: 1.2;
    color: $footer-text;
  }

  .footer-content {
    display: grid;
    grid-template-columns: 1.2fr 1fr 1.3fr;
    grid-template-areas: "footer-center footer-right footer-left";
    column-gap: clamp(1.333rem, 3.333vw, 5.333rem);
    row-gap: $space-4;
    text-align: left;
    max-width: 1800px;
    margin: 0 auto;
    width: 98%;
    align-items: start;
  }

  .footer-left   { grid-area: footer-left;   align-self: start; }
  .footer-center { grid-area: footer-center; align-self: start; }
  .footer-right  { grid-area: footer-right;  align-self: start; }

  .footer-subtitle {
    font-weight: 700;
    font-size: clamp(1.05rem, 2.8vw, 1.2rem);
    color: $footer-text;
    margin-bottom: 0.25rem;
    line-height: 1.1;
  }

  .footer-left {
    margin-top: calc(-1 * #{$space-3});
    padding-left: $space-3;

    .footer-menu {
      padding-top: 0;

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          margin-bottom: $space-1;

          a {
            color: $footer-link;
            text-decoration: none;
            font-size: clamp(1.15rem, 2.8vw, 1.4rem);
            font-weight: 400;
            transition: all 0.3s ease;
            display: inline-block;

            @media (hover: hover) {
              &:hover {
                color: $footer-link-hover;
                transform: translateX(4px);
              }
            }
          }
        }
      }
    }
  }

  .society-items {
    .society-item {
      margin-bottom: 0.3rem;
      font-size: clamp(0.95rem, 2.4vw, 1.1rem);
      font-weight: 300;
      line-height: 1.4;
      color: rgba($footer-text, 0.9);

      strong { font-weight: 300; }

      a {
        color: $footer-link;
        text-decoration: none;
        @media (hover: hover) { &:hover { color: $footer-link-hover; } }
      }
    }
  }

  .contact-items {
    .contact-item {
      margin-bottom: 0.3rem;
      font-size: clamp(0.95rem, 2.4vw, 1.1rem);
      font-weight: 300;
      line-height: 1.4;
      color: rgba($footer-text, 0.9);

      strong { font-weight: 300; color: $footer-text; }

      a {
        color: $footer-link;
        text-decoration: none;
        @media (hover: hover) { &:hover { color: $footer-link-hover; } }
      }
    }
  }

  .footer-bottom-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 1800px;
    width: 98%;
    margin: $space-3 auto 0 auto;

    .footer-bottom {
      font-size: $font-xs;
      color: rgba($clr-white, 0.8);
      line-height: 1.4;
      font-weight: 300;
      text-align: center;
    }
  }
}

@media (min-width: 1301px) {
  .site-footer {
    padding-left: calc(#{$space-1} * 2 / 3);
    padding-right: calc(#{$space-1} * 2 / 3);
  }
  .site-footer .footer-heading,
  .site-footer .footer-content,
  .site-footer .footer-bottom-wrapper {
    width: 98.6667%;
  }
}

@media (max-width: 1300px) {
  .site-footer {
    padding: $space-6 $space-0-5 $space-2-5 $space-0-5;

    .footer-heading {
      max-width: 1600px;
      width: 96%;
    }

    .footer-content {
      width: 96%;
      max-width: 1600px;
      column-gap: clamp(1rem, 3vw, 3rem);
      grid-template-columns: 1.2fr 1.3fr 0.9fr;
    }

    .footer-left {
      padding-left: $space-1;
    }

    .footer-bottom-wrapper {
      width: 96%;
      max-width: 1600px;
    }
  }
}

@media (max-width: 1000px) {
  .site-footer {
    .footer-base {
      overflow-x: hidden;
      overflow-y: visible;
      left: 0;

      img {
        width: 200%;
        max-width: none;
      }
    }
  }
}

@media (max-width: 768px) {
  .site-footer {
    padding: $space-4 $space-0-5 $space-1-5 $space-0-5;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas:
      "footer-left"
      "footer-heading"
      "footer-center"
      "footer-right"
      "footer-bottom";
    justify-items: center;
    row-gap: $space-2-5;

    .footer-base {
      img {
        width: 260%;
      }
    }

    .footer-heading {
      grid-area: footer-heading;
      width: 88%;
      max-width: 88%;
      text-align: center;
      margin: 0 auto;
    }

    .footer-content {
      display: contents;
    }

    .footer-left   { grid-area: footer-left;   width: 88%; text-align: center; }
    .footer-center { grid-area: footer-center; width: 88%; text-align: center; }
    .footer-right  { grid-area: footer-right;  width: 88%; text-align: center; }

    .footer-bottom-wrapper { grid-area: footer-bottom; width: 88%; }

    .footer-left,
    .footer-center,
    .footer-right {
      .footer-subtitle { text-align: center; }
    }

    .footer-left {
      .footer-menu {
        padding-top: $space-1-5;
        
        ul {
          justify-content: center;
          gap: $space-0-5 $space-1-5;

          li {
            &:not(:last-child)::after {
              content: none;
              margin-left: 0;
              opacity: 0;
            }
          }
        }
      }
    }

    .society-items { text-align: center; }
    .contact-items { text-align: center; }
  }
}

@media (max-width: 480px) {
  .site-footer {
    padding: $space-3-5 $space-0-5 $space-1 $space-0-5;

    .footer-base {
      img {
        width: 320%;
      }
    }

    .footer-heading { width: 85%; max-width: 85%; }
    .footer-left, .footer-center, .footer-right { width: 85%; }
    .footer-bottom-wrapper { width: 85%; margin-top: $space-2; }

    .footer-left {
      .footer-menu {
        padding-top: $space-1-5;
        
        ul {
          flex-direction: column;
          gap: $space-0-5;
          align-items: center;

          li { &:not(:last-child)::after { display: none; } }
        }
      }
    }
  }
}

@media (max-width: 360px) {
  .site-footer {
    padding: $space-3 $space-0-5 $space-1 $space-0-5;

    .footer-base {
      img {
        width: 340%;
      }
    }

    .footer-heading { width: 82%; max-width: 82%; }
    .footer-left, .footer-center, .footer-right { width: 82%; }
    .footer-bottom-wrapper { width: 82%; }
  }
}
</style>
